import { useDebouncedValue }                               from "@mantine/hooks";
import { useRouter }                                       from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { v1 }                                              from "uuid";

import { useGetProfileData }      from "_common/hooks/auth";
import { useSetHasVotedAtom }     from "_common/hooks/polls";
import { useGetSessionEnded }     from "_common/hooks/stream";
import { isPageRoute, removeKey } from "_common/utils";
import { useGetMessage }          from "_components/features/ivsChat/states";
import {
	useGetIsStreamEnded,
	useGetPollsEndedData,
	useGetPollsOpen,
	useGetPollsStartedData,
	useSetPollsOpen
} from "_components/pages/studio/states";
import {
	COMMENT_TRIM_COUNT,
	DEBOUNCE_COMMENTS_IN_MS,
	MAX_COMMENTS_LIMIT
} from "_config/index";
import { useGetUpdateSession } from "_jotai/liveSession";

import { useChat }  from "./hooks";
import Presentation from "./presentation";

export const FAN_LEADERBOARD_INITIAL_STATE = {
	isLive : false,
	date   : undefined
};

const ChatContainer = props => {

	const {
		leaderboardData,
		theatreModeToggle,
		isStreamer,
		broadcastId,
		chatPoppedOut,
		isFromLayout,
		isMobile,
		handleDisabledChatInputClick,
		previousComments
	} = props;
	const userProfile                          = useGetProfileData ();
	const polls                                = useGetPollsOpen ();
	const togglePolls                          = useSetPollsOpen ();
	const [ activeTab, setActiveTab ]          = useState ( "CHAT" );
	const [ autoScrollEnabled, setAutoScroll ] = useState ( true );
	const updateHasVoted                       = useSetHasVotedAtom ();
	const pollsEndData                         = useGetPollsEndedData ();
	const pollsStartData                       = useGetPollsStartedData ();

	const {
		myMutedStateData,
		topDonor,
		removeDonor,
		emotesList,
		sendUpdatedComments,
		init,
		clearListeners
	} = useChat ( { broadcastId, isStreamer } );

	useEffect ( () => {
		if ( pollsEndData.id )
			updateComments ( pollsEndData );
	}, [ pollsEndData.id ] );

	useEffect ( () => {
		if ( pollsStartData.id )
			updateComments ( pollsStartData );
	}, [ pollsStartData.id ] );

	const router         = useRouter ();
	const isStudioPage   = isPageRoute ( { router, page: "studio" } );
	const isStreamEnded  = useGetIsStreamEnded ();
	const sessionData    = useGetUpdateSession ();
	const pollsEnabled   = sessionData?.pollsEnabled;
	const isSessionEnded = useGetSessionEnded ();
	const message        = useGetMessage ();

	const [ commentsState, setCommentsState ]  = useState ( previousComments );
	const [ commentsList, updateCommentsList ] = useState ( previousComments );
	const commentsRef                          = useRef ( [] );
	const ownCommentsMapRef                    = useRef ( {} );

	useEffect ( () => {
		if ( !message )
			return;

		ivsMessages ( message );
	}, [ message ] );

	const [ batchData, setBatchData ] = useState ( [] );

	const [ debouncedBatchData ] = useDebouncedValue ( batchData, DEBOUNCE_COMMENTS_IN_MS );

	const processBatchData = data => {
		setBatchData ( prevBatchData => [ ...prevBatchData, { ...data, uniqueID: v1 () } ] );
	};

	useEffect ( () => {
		if ( !batchData.length )
			return;

		setCommentsState ( prevState => {

			if ( prevState.length + batchData.length >= MAX_COMMENTS_LIMIT ) {
				return [ ...prevState, ...batchData ].slice ( -COMMENT_TRIM_COUNT );
			}

			return [ ...prevState, ...batchData ];

		} );

		setBatchData ( [] );

	}, [ debouncedBatchData ] );

	let showPolls = pollsEnabled && !isSessionEnded; // enable polls for all types of stream

	useEffect ( () => {
		if ( !broadcastId )
			return;

		init ();

		return () => {
			clearListeners ();
			updateHasVoted ( {} );
		};
	}, [ broadcastId ] );

	const ivsMessages = message => {
		const eventName = message?.attributes?.eventName;
		let commentText = message?.content;

		if ( commentText === "NO_CONTENT" ) {
			commentText = "";
		}

		// const metadata = message?.attributes?.metadata;

		let metadata = null;

		try {
			if ( message?.attributes?.metadata && message.attributes.metadata.trim () !== "" ) {
				metadata = JSON.parse ( message.attributes.metadata );
			}
		} catch ( error ) {
			// If JSON parsing fails
			return;
		}

		if ( !metadata )
			return;

		const __data = metadata;

		if ( eventName === "SEND_MESSAGE" ) {

			if ( ( __data?.sportsFan?.id === userProfile?.id ) ) {

				if ( __data?.commentType === "STREAM_DONATION" ) {

					return updateComments ( __data );

				}
				if ( __data?.requestId === null || __data?.commentType === "MAGIC_CHAT" ) {

					return updateComments ( { ...__data, commentText } );
				}

				let checkCommentPreExists = commentsRef?.current?.find ( com => com.requestId === __data?.requestId );

				if ( checkCommentPreExists ) {
					ownCommentsMapRef.current = removeKey ( ownCommentsMapRef.current, checkCommentPreExists.requestId );

					let newComment = commentsRef?.current?.map ( comment => {
						if ( comment?.requestId == __data?.requestId ) {

							return { ...__data, commentText };
						}

						return comment;
					} );

					return setCommentsState ( newComment );

				} else {
					let updatedData = { ...__data, commentText };

					return updateComments ( updatedData );
				}

			}
			let updatedData = { ...__data, commentText };

			if ( __data ) {

				updateComments ( updatedData );
			}
		}

		if ( eventName === "CHAT_TIPS" ) {

			// dont render squad play or affiliate ad chat tips
			if ( __data?.chatTip?.ctaType === "AFFL_AD" || __data?.chatTip?.ctaType === "SQUAD_PLAY" )
				return;

			if ( __data ) {
				updateComments ( __data );
			}
		}

		if ( eventName === "DELETE_MESSAGE" ) {
			let comment = {};

			if ( __data?.deletedCommentId ) {
				comment["id"] = __data?.deletedCommentId;
				removeComment ( comment );
			}

		}

		if ( eventName === "USER_BLOCKED" ) {

			if ( __data?.authorId ) {
				const updatedComments = commentsRef.current.filter ( item => item.sportsFan?.id !== __data.authorId );

				setCommentsState ( updatedComments );

			}
		}
	};

	const removeComment = comment => {
		if ( !comment )
			return;

		const updatedComments = commentsRef.current.filter ( item => item.id !== comment.id );

		setTimeout ( () => {
			setCommentsState ( updatedComments );
		}, 1000 );

	};

	const toggleUpdatedComments = useCallback ( value => {
		sendUpdatedComments.set ( value );
	}, [] );

	useEffect ( () => {
		commentsRef.current = commentsState;

		updateCommentsList ( commentsState );
	}, [ commentsState ] );

	const updateComments = comment => {
		if ( !sendUpdatedComments || !comment )
			return;

		processBatchData ( comment );
	};

	const deleteComment = useCallback ( selectedComment => {
		const updatedComments = commentsList.map ( comment => {
			if ( comment.id === selectedComment.id ) {
				comment.isDeletedByUser = true;
			}

			return comment;
		} );

		updateCommentsList ( updatedComments );
	}, [ commentsList ] );

	const handleCommentOptimistic = ( { comment: commentData, type } ) => {
		switch ( type ) {
			case "add":
				setCommentsState ( prevState => {
					let newCommentState = [ ...prevState, ...batchData, { ...commentData, uniqueID: v1 () } ];

					if ( prevState.length >= MAX_COMMENTS_LIMIT ) {
						newCommentState = [ ...prevState, ...batchData, commentData ].slice ( -COMMENT_TRIM_COUNT );
					}

					commentsRef.current = newCommentState;

					return newCommentState;

				} );

				setBatchData ( [] );
				ownCommentsMapRef.current = { ...ownCommentsMapRef.current, [commentData.requestId]: 1 };

				return;

			case "remove":
				setCommentsState ( prevState => {
					let newComments = prevState?.filter ( comment => comment?.requestId !== commentData.requestId );

					commentsRef.current = newComments;

					return newComments;
				} );

				ownCommentsMapRef.current = removeKey ( ownCommentsMapRef.current, [ commentData.requestId ] );

				return;

			default:
				break;
		}
	};

	return (
		<Presentation
			activeTab = { activeTab }
			autoScrollEnabled = { autoScrollEnabled }
			broadcastId = { broadcastId }
			chatPoppedOut = { chatPoppedOut }
			comments = { commentsList }
			deleteComment = { deleteComment }
			displayModal = { props?.displayModal }
			donationOption = { props?.donationOption }
			emotesList = { emotesList.value }
			handleCommentOptimistic = { handleCommentOptimistic }
			handleDisabledChatInputClick = { handleDisabledChatInputClick }
			handleModal = { props?.handleModal }
			isFromLayout = { isFromLayout }
			isMobile = { isMobile }
			isSessionEnded = { isStudioPage ? isStreamEnded : isSessionEnded }
			isStreamer = { isStreamer }
			leaderboardData = { leaderboardData }
			myMutedStateData = { myMutedStateData }
			polls = { polls }
			removeDonor = { removeDonor }
			setActiveTab = { setActiveTab }
			setAutoScroll = { setAutoScroll }
			setCommentsState = { setCommentsState }
			showPolls = { showPolls }
			theatreModeToggle = { theatreModeToggle }
			togglePolls = { togglePolls }
			toggleUpdatedComments = { toggleUpdatedComments }
			topDonor = { topDonor }
			userProfile = { userProfile }
		/>
	);
};

export default ChatContainer;
